/**
 * Minimal in-app toast system — no external dependency.
 *
 * Usage:
 *   const { toasts, addToast } = useToast()
 *   addToast('Approved', 'success')
 *   // render <ToastContainer toasts={toasts} />
 */
import { useState, useCallback } from 'react'

export type ToastVariant = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  message: string
  variant: ToastVariant
}

let _id = 0

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = ++_id
    setToasts(prev => [...prev, { id, message, variant }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 4000)
  }, [])

  return { toasts, addToast }
}

function toastClass(variant: ToastVariant): string {
  switch (variant) {
    case 'success': return 'bg-green-600 text-white'
    case 'error':   return 'bg-red-600 text-white'
    default:        return 'bg-gray-800 text-white'
  }
}

export function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`px-4 py-2 rounded-lg shadow-lg text-sm font-medium pointer-events-auto transition-all ${toastClass(t.variant)}`}
        >
          {t.message}
        </div>
      ))}
    </div>
  )
}
